import { FloatingTabControl } from "@components/ui/FloatingTabs"
import { Spinner } from "@components/ui/Spinner"
import { TabControl, TabControlProps } from "@components/ui/TabControl"
import { useScrollPagination } from "@hooks/useScrollPagination"
import { AnimatePresence, AnimationProps, motion } from "motion/react"
import { ReactNode, useEffect, useRef } from "react"
import { StickyToFixed } from "./StickyToFixed"
import { useSiteTheme } from "@features/header/header-theme"

type Props = {
  filters?: ReactNode
  tabs?: TabControlProps
  loading?: boolean
  noResults?: boolean
  animationKey?: string
  scrollKey?: string
  header?: ReactNode
  itemsPerPage: number
  totalItems: number
  fixedHeader?: boolean
  children: ReactNode | ((props: { page: number; sensor: ReactNode }) => ReactNode)
}

export function ResultBrowser(props: Props) {
  const scrollTarget = useRef<HTMLDivElement>(null!)
  const pagination = useScrollPagination(
    {
      itemsPerPage: props.itemsPerPage,
      totalItems: props.totalItems,
    },
    [props.animationKey],
  )

  const animProps: AnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0, transition: { duration: 0.1 } },
    transition: { duration: 0.2 },
  }

  function scrollToTop() {
    const scrollTop = Math.min(
      scrollTarget.current.offsetTop - (document.getElementById("site-header")?.clientHeight ?? 0),
      document.scrollingElement?.scrollTop!,
    )
    document.scrollingElement?.scrollTo({
      top: scrollTop,
    })
  }

  useEffect(() => {
    if (props.scrollKey) {
      scrollToTop()
    }
  }, [props.scrollKey])

  const pageTheme = useSiteTheme().page

  return (
    <div>
      {/* Scroll target — to scroll to the top when filters change */}
      <div ref={scrollTarget} />

      {/* Filter bar */}
      {!!(props.filters || props.tabs) && (
        <StickyToFixed
          enabled={props.fixedHeader}
          className="results-browser-filter-bar left-0 right-0 sticky wp:static top-headerHeight z-10 bg-bg transition-colors"
        >
          <div className="scrollbars-hidden md:mx-auto md:w-grid flex flex-row justify-between items-center max-md:overflow-auto py-4">
            <motion.div
              layoutRoot
              className="flex gap-2 max-md:[&>*]:flex-none max-md:gap-3 max-md:flex-none max-md:w-ful max-md:px-grid-margin"
            >
              {props.filters}
            </motion.div>
            <motion.div layoutRoot className="hidden md:flex gap-2">
              {props.tabs && <TabControl {...props.tabs} />}
            </motion.div>
          </div>
        </StickyToFixed>
      )}

      {/* Mobile floating tab bar */}
      {!!props.tabs && <FloatingTabControl {...props.tabs} theme={pageTheme == "dark" ? "light" : "dark"} />}

      {/* Header/Result info */}
      {props.header}

      <AnimatePresence
        mode="wait"
        onExitComplete={() => {
          scrollToTop()
        }}
      >
        {props.loading ? (
          <motion.div key="loading" className="min-h-[30vh] flex items-center justify-center" {...animProps}>
            <Spinner />
          </motion.div>
        ) : props.noResults ? (
          <motion.div key="noResults" className="w-grid mx-auto text-center py-10 type-title-xl" {...animProps}>
            No results found.
          </motion.div>
        ) : (
          <motion.div key={props.animationKey} {...animProps}>
            {typeof props.children === "function" ? props.children(pagination) : props.children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
